import { AutoAwesome, ChatBubbleOutline, Circle, Euro, TrendingUp, WarningAmber } from '@mui/icons-material';
import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, Grid, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, ChangeEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../utils/misc';
import { NumberInput } from '../../../components/inputs';
import { NegotiationParams } from '../../../components/dialogs';
import NegotiationDetails from './NegotiationDetails';
import { OfferStatus, PotentialOffer, TruckDetails } from '../../../types';
import { Caption, H3 } from '../../../components/typography';
import OfferDetails from './OfferDetails';
import OfferActionButton from '../../../components/tables/potentialMatchesTable/OfferActionButton';
import { NegotiationActions, Planning } from '../../../components/dialogs/negotiations/negotiationDialog';
import { InfoItem } from '../../../components/misc/InfoItem';
import { useNegotiationHandlers } from './useNegotiationHandlers';
import { CustomTooltip } from '../../../components/misc/CustomTooltip';
import { Negotiation } from '../../../models/negotiation';
import NumberField from '../../../components/inputs/NumberField';
import { apiGet, apiPost } from '../../../services/apiClient';

interface UpdatedPrices {
  minimumAcceptancePrice?: number;
  fairPrice?: number;
  targetPrice?: number;
}

interface NegotiationFormProps {
  negotiation: any;
  offer?: PotentialOffer;
  vehicle: TruckDetails | null;
  formData: Partial<NegotiationParams> | null;
  planning: Planning | undefined;
  message: string;
  targetPriceMarginFactor: number;
  errors: {
    estimatedPrice?: string;
    fairPrice?: string;
    minimumAcceptance?: string;
    targetPrice?: string;
    minAccept?: string;
  };
  actions: NegotiationActions;
  arePricesUpdated: boolean;
  setArePricesUpdated: React.Dispatch<React.SetStateAction<boolean>>;
  onRefresh: () => void;
  setNegotiation: React.Dispatch<React.SetStateAction<Negotiation | null>>;
  setNegotiationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<NegotiationParams> | null>>;
  includePlates: boolean;
  setIncludePlates: React.Dispatch<React.SetStateAction<boolean>>;
  useMessenger?: boolean;
  hasMessenger?: boolean;
}

const NegotiationForm = ({
  negotiation,
  offer,
  vehicle,
  formData,
  planning,
  message,
  targetPriceMarginFactor,
  errors,
  actions,
  arePricesUpdated,
  setArePricesUpdated,
  onRefresh,
  setNegotiation,
  setNegotiationModalOpen,
  setMessage,
  setFormData,
  includePlates,
  setIncludePlates,
  useMessenger,
  hasMessenger,
}: NegotiationFormProps) => {

  const { t } = useTranslation();
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const textareaScrollRef = useRef<HTMLTextAreaElement | null>(null);

  const [tab, setTab] = React.useState(0);
  const [isTextareaScrolling, setIsTextareaScrolling] = React.useState<boolean>(false);
  const textareaScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    handleOrderConfirmNegotiation,
    acceptNegotiationCore,
    handleAbortNegotiation,
    ConfirmNegotiationModal } = useNegotiationHandlers();


  // Scroll down the chat to the last message when modal opens or messages change
  useEffect(() => {
    if (messagesScrollRef.current) {
      messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
    }
  }, [negotiation?.messages, negotiation?.emailBodies, negotiation, messagesScrollRef]);

  // Handle textarea scroll events to show/hide scrollbar
  const handleTextareaScroll = () => {
    setIsTextareaScrolling(true);
    if (textareaScrollTimeoutRef.current) {
      clearTimeout(textareaScrollTimeoutRef.current);
    }
    textareaScrollTimeoutRef.current = setTimeout(() => {
      setIsTextareaScrolling(false);
    }, 1000); // Hide scrollbar 1 second after scrolling stops
  };

  useEffect(() => {
    const textarea = textareaScrollRef.current;
    if (textarea) {
      textarea.addEventListener('scroll', handleTextareaScroll);
      return () => {
        textarea.removeEventListener('scroll', handleTextareaScroll);
        if (textareaScrollTimeoutRef.current) {
          clearTimeout(textareaScrollTimeoutRef.current);
        }
      };
    }
  }, []);

  const handleChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Check if negotiations are already started
  const hasEmailBodies = negotiation?.emailBodies && negotiation.emailBodies.length > 0;

  const handleMessageChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  }, [setMessage]);

  const negotiationStrategy = (nego: NegotiationParams) => nego.badSituation ? t('Low Profite') : nego.goodSituation ? t('High Profite') : t('Normal');

  const needAction = (offer: PotentialOffer | undefined) => offer?.negotiations && offer?.negotiations[0]?.hasUnsent;

  const updatePrices = (updatedPrices: Partial<UpdatedPrices>) => {
    setNegotiation({
      ...negotiation,
      ...updatedPrices
    });
    setArePricesUpdated(true);
  };

  const commitPrices = async () => {
    apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${negotiation.vehicle_id}/negotiation/${negotiation.id}/update_prices`, {
      minimumAcceptancePrice: negotiation.minimumAcceptancePrice,
      fairPrice: negotiation.fairPrice,
      targetPrice: negotiation.targetPrice,
    });
    setArePricesUpdated(false);
  };

  const discardPrices = async () => {
    const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${negotiation.vehicle_id}/negotiation/${negotiation.id}/info`);
    const data = await response.json();
    setNegotiation(data.result);
    setArePricesUpdated(false);
  };

  if (!negotiation) return (
    <Stack sx={{ height: '100%', overflow: 'auto' }}>
      {/* Overtime warning */}
      {typeof planning !== 'undefined' && planning?.overtime && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmber color="warning" />
          <Typography variant="body2" color="warning.main">
            {t(`Warning: Driver cannot legally deliver this load before ${planning.schedule[planning.schedule.length - 1].endTime.toLocaleString()}.`)}
          </Typography>
        </Box>
      )}
      <Form
        formData={formData}
        handleChange={handleChange}
        errors={errors}
        targetPriceMarginFactor={targetPriceMarginFactor}
        offer={offer}
        useMessenger={useMessenger}
        hasMessenger={hasMessenger}
      />
    </Stack>
  );

  return (
    <Box sx={{ height: 'calc(100% - 48px)' }}>
      <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
        <Tab label={t('Negotiation')} />
        <Tab label={t('Details')} />
        <Tab label={t('Offer')} />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2} sx={{ height: '100%', px: 2, py: 2 }}>
          <Grid size={{ xs: 12, md: 3 }} sx={{ height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
              <Stack direction={'row'} spacing={1.5} alignItems="center">
                <H3>{t('Status')}</H3>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {needAction(offer) && (
                        <Tooltip title={t('Requires Attention')}>
                          <Circle sx={{
                            fontSize: '0.625rem',
                            color: (_theme: any) => {
                              if (!offer) return 'orange';
                              const isStarted = offer.status === OfferStatus.NEGOTIATION_STARTED ||
                                offer.status >= OfferStatus.NEGOTIATION_STARTED;
                              return isStarted
                                ? '#4CAF50'
                                : 'orange';
                            }
                          }} />
                        </Tooltip>
                      )}
                      {offer ? OfferStatus[offer.status]?.replace('NEGOTIATION_', '').replaceAll('_', ' ') : ''}
                    </Box>
                  }
                  size="small"
                  sx={{
                    height: 26,
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    bgcolor: (theme: any) => {
                      if (!offer) return 'transparent';
                      const isStarted = offer.status === OfferStatus.NEGOTIATION_STARTED ||
                        offer.status >= OfferStatus.NEGOTIATION_STARTED;
                      return isStarted
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.12)')
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)');
                    },
                    color: (theme: any) => {
                      if (!offer) return 'inherit';
                      const isStarted = offer.status === OfferStatus.NEGOTIATION_STARTED ||
                        offer.status >= OfferStatus.NEGOTIATION_STARTED;
                      return isStarted
                        ? (theme.palette.mode === 'dark' ? '#9BE7B0' : '#2e7d32')
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)');
                    },
                    border: '1px solid',
                    borderColor: (theme: any) => {
                      if (!offer) return 'transparent';
                      const isStarted = offer.status === OfferStatus.NEGOTIATION_STARTED ||
                        offer.status >= OfferStatus.NEGOTIATION_STARTED;
                      return isStarted
                        ? (theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.25)' : 'rgba(76,175,80,0.2)')
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)');
                    },
                  }}
                />
              </Stack>
              <Stack direction={'row'} spacing={1} alignItems="center">
                <H3>{t('Actions')}</H3>
                {offer ? <OfferActionButton
                  offer={offer}
                  vehicle={vehicle}
                  onRefresh={onRefresh}
                  onAccept={async (vehicle, offer) => {
                    await acceptNegotiationCore(vehicle, offer, false, true, message);
                    setNegotiationModalOpen(false);
                  }}
                  onOrderConfirm={async (vehicle, offer) => {
                    await handleOrderConfirmNegotiation(vehicle, offer);
                    setNegotiationModalOpen(false);
                  }}
                  onAbort={async (vehicle, offer) => {
                    await handleAbortNegotiation(vehicle, offer);
                    setNegotiationModalOpen(false);
                  }}
                  onClose={actions.handleCloseNegotiation}
                  onStart={actions.handleOpenNegotiationModal}
                /> : <Caption>
                  {t('No actions available')}
                </Caption>}
              </Stack>
              <InfoItem title={t('Negotiation Strategy')} value={negotiationStrategy(negotiation)} />
              <NumberField
                label={t('Minimum Acceptance')}
                min={0}
                smallStep={0.01}
                value={negotiation.minimumAcceptancePrice}
                onValueCommitted={(minimumAcceptancePrice) => {
                  if (minimumAcceptancePrice !== null) {
                    updatePrices({
                      minimumAcceptancePrice: minimumAcceptancePrice
                    });
                  }
                }}
              />
              <NumberField
                label={t('Fair Price')}
                min={0}
                smallStep={0.01}
                value={negotiation.fairPrice}
                onValueCommitted={(fairPrice) => {
                  if (fairPrice !== null) {
                    updatePrices({
                      fairPrice: fairPrice
                    });
                  }
                }}
              />
              <NumberField
                label={t('Target Price')}
                min={0}
                smallStep={0.01}
                value={negotiation.targetPrice}
                onValueCommitted={(targetPrice) => {
                  if (targetPrice !== null) {
                    updatePrices({
                      targetPrice: targetPrice
                    });
                  }
                }}
              />
              {arePricesUpdated && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info">{t('You must update prices or discard pending changes before sending emails.')}</Alert>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button fullWidth variant="outlined" color="error" onClick={discardPrices}>{t('Discard changes')}</Button>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button fullWidth variant="outlined" onClick={commitPrices}>{t('Update prices')}</Button>
                  </Grid>
                </Grid>
              )}
              <InfoItem title={t('Last Offered Price')} value={negotiation.lastOfferedPriceUs} />
              <InfoItem title={t('Last Offered Price by Client')} value={negotiation.lastOfferedPriceClient} />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 9 }} height={'100%'}>
            <Box ref={messagesRef} height={'100%'} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box
                ref={messagesScrollRef}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                  scrollbarWidth: 'thin',
                  msOverflowStyle: 'scrollbar',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                    transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                    borderRadius: '100px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: (theme: any) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '100px',
                    '&:hover': {
                      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                    },
                  },
                }}
              >
                <Messages
                  hasEmailBodies={hasEmailBodies}
                  negotiation={negotiation}
                />
              </Box>
              <Box sx={{ mt: 2, flexShrink: 0 }}>
                {useMessenger && (
                  <Alert severity="warning" icon={<ChatBubbleOutline />} sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      icon={<ChatBubbleOutline />}
                      label={t('Chat — Timocom Messenger')}
                      sx={{ mr: 1 }}
                    />
                    {t('Replies via Timocom chat are not auto-imported — monitor the portal manually.')}
                  </Alert>
                )}
                <MessageInput
                  message={message}
                  handleMessageChange={handleMessageChange}
                  isTextareaScrolling={isTextareaScrolling}
                  textareaScrollRef={textareaScrollRef}
                />
                {offer?.status === OfferStatus.NEGOTIATION_WAITING_FINAL_CONFIRM && (
                  <FormControlLabel
                    control={<Checkbox checked={includePlates} onChange={(e) => setIncludePlates(e.target.checked)} />}
                    label={
                      <Box>
                        <Typography variant="body2">{t('Include Plate Numbers')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('Append truck and trailer plate numbers to the acceptance email')}
                        </Typography>
                      </Box>
                    }
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <NegotiationDetails
          negotiation={negotiation}
          strategy={negotiationStrategy(negotiation)}
        />
      )}

      {tab === 2 && (
        <OfferDetails offer={offer} />
      )}

      {ConfirmNegotiationModal}

    </Box>
  );
};

const ProfitSummary = ({
  formData,
  targetPriceMarginFactor
}: {
  formData: Partial<NegotiationParams> | null,
  targetPriceMarginFactor: number
}) => {
  const { t } = useTranslation();
  if (!formData?.fairPrice || !formData?.targetPrice || !formData?.estimatedPrice) return null;
  if (!formData.minAccept) formData.minAccept = 0;
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: 'background.grey',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'background.darkGrey',
        flex: 1
      }}
    >
      <InfoItem
        title={t('Estimated → Min Accept:')}
        value={`€${(formData.minAccept - formData.estimatedPrice).toFixed(2)}`}
      />
      <InfoItem
        title={t('Min Accept → Fair:')}
        value={`€${(formData.fairPrice - formData.minAccept).toFixed(2)}`}
      />
      <InfoItem
        title={t('Fair → Target:')}
        value={`€${(formData.targetPrice - formData.fairPrice).toFixed(2)}`}
      />
      <InfoItem
        title={t('Total Difference:')}
        value={`€${(formData.targetPrice - formData.estimatedPrice).toFixed(2)}`}
      />
      <InfoItem
        title={t('Target price factor:')}
        value={`${(targetPriceMarginFactor * 100).toFixed(0)}% (from company settings)`}
      />
    </Box>
  );
};

export default NegotiationForm;

const Form = ({
  formData,
  handleChange,
  errors,
  targetPriceMarginFactor,
  disableInputs,
  offer,
  useMessenger,
  hasMessenger,
}: {
    formData: Partial<NegotiationParams> | null,
    handleChange: (key: string, value: any) => void,
    errors: {
        estimatedPrice?: string;
        fairPrice?: string;
        minimumAcceptance?: string;
        targetPrice?: string;
        minAccept?: string;
    },
    targetPriceMarginFactor: number,
    disableInputs?: boolean,
    offer?: PotentialOffer,
    useMessenger?: boolean,
    hasMessenger?: boolean,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Grid container spacing={2} mt={1}>
        <Grid size={{ xs: 12, md: 4 }}>
          <NumberInput
            fullWidth
            label={t('Estimated Price')}
            value={formData?.estimatedPrice}
            disabled
            size="small"
            error={!!errors.estimatedPrice}
            helperText={errors.estimatedPrice || t('Estimated market price for this load (excluding empty kilometers)')}
            InputProps={{
              startAdornment: <Euro sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: 'text.secondary', mr: 1 }} />
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <NumberInput
            fullWidth
            label={t('Fair Price')}
            value={formData?.fairPrice}
            disabled={disableInputs}
            size="small"
            onChange={(e) => handleChange('fairPrice', parseFloat(e.target.value) || 0)}
            error={!!errors.fairPrice}
            helperText={errors.fairPrice || t('Recommended price for this load (including empty kilometers)')}
            InputProps={{
              startAdornment: <Euro sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: 'text.secondary', mr: 1 }} />
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <NumberInput
            fullWidth
            label={t('Target Price')}
            value={formData?.targetPrice}
            disabled={disableInputs}
            size="small"
            onChange={(e) => handleChange('targetPrice', parseFloat(e.target.value) || 0)}
            error={!!errors.targetPrice}
            helperText={errors.targetPrice || t(`Your target price for negotiation (auto-calculated as ${(targetPriceMarginFactor * 100).toFixed(0)}% of the fair price)`)}
            InputProps={{
              startAdornment: <TrendingUp sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: 'text.secondary', mr: 1 }} />
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <NumberInput
            fullWidth
            label={t('Minimum Acceptance Price')}
            size="small"
            disabled={disableInputs}
            value={formData?.minAccept}
            onChange={(e) => handleChange('minAccept', parseFloat(e.target.value) || 0)}
            error={!!errors.minAccept}
            helperText={errors.minAccept || t('Lowest price you are willing to accept for this load')}
            InputProps={{
              startAdornment: <Euro sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: 'text.secondary', mr: 1 }} />
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={0.75}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData?.badSituation}
                  disabled={disableInputs}
                  onChange={(e) => handleChange('badSituation', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('Bad Situation')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Enable if this is a special or urgent negotiation scenario')}
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData?.goodSituation}
                  disabled={disableInputs}
                  onChange={(e) => handleChange('goodSituation', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('Good Situation')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Enable if this is a favorable negotiation scenario')}
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData?.autoRespond}
                  disabled={disableInputs}
                  onChange={(e) => handleChange('autoRespond', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('Auto Respond')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Enable automatic responses during negotiation')}
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData?.userOwnName}
                  disabled={disableInputs}
                  onChange={(e) => handleChange('userOwnName', e.target.checked)}
                  sx={{
                    color: 'text.black'
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('User Own Name')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Use your own name in negotiations')}
                  </Typography>
                </Box>
              }
            />

            {process.env.REACT_APP_ENV !== 'production' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData?.localNego}
                    disabled={disableInputs}
                    onChange={(e) => handleChange('localNego', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {t('Local Negotiation')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Enable if this is local negotiation and you don\'t want to send a mail (dev only)')}
                    </Typography>
                  </Box>
                }
              />
            )}

            {hasMessenger && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!useMessenger}
                    disabled={disableInputs || !offer?.contactPerson?.email}
                    onChange={(e) => {
                      const next = e.target.checked;
                      handleChange('useMessenger', next);
                      if (next) {
                        handleChange('freightExchangeFormat', false);
                      } else if (offer?.externalPlatform === 'TimocomMarketplace') {
                        handleChange('freightExchangeFormat', true);
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {t('Send via Timocom Chat')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {offer?.contactPerson?.email
                        ? t('Dispatch through the Timocom Messenger instead of email. Replies are not auto-imported — monitor the portal manually.')
                        : t('This contact has no email. Messages will be dispatched via Timocom Messenger. Replies are not auto-imported — monitor the portal manually.')}
                    </Typography>
                  </Box>
                }
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData?.autoSendInitial ?? true}
                  disabled={disableInputs}
                  onChange={(e) => handleChange('autoSendInitial', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('Auto Send Initial')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Send the first negotiation message immediately, even when auto-respond is off')}
                  </Typography>
                </Box>
              }
            />

            {offer?.externalPlatform?.endsWith('Marketplace') && !useMessenger && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData?.freightExchangeFormat}
                    disabled={disableInputs}
                    onChange={(e) => handleChange('freightExchangeFormat', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {t('Freight Exchange Format')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Use freight exchange email format with structured offer details appended to the email')}
                    </Typography>
                  </Box>
                }
              />
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ProfitSummary
            formData={formData}
            targetPriceMarginFactor={targetPriceMarginFactor}
          />
        </Grid>
      </Grid>
    </>
  );
};

const Messages = ({
  negotiation,
  hasEmailBodies,
}: {
  negotiation: Negotiation;
  hasEmailBodies: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {hasEmailBodies ? (
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            border: '1px solid border.default',
            borderRadius: 2,
            p: 3,
            backgroundColor: 'transparent',
            minHeight: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            '&::-webkit-scrollbar': {
              width: '0px',
              height: '0px',
              display: 'none',
            },
            '&::-webkit-scrollbar-track': {
              display: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}>
            {negotiation.emailBodies && negotiation.emailBodies
              .sort((a: any, b: any) => a.at - b.at)
              .map((email, index: number) => (
                <Box key={index} sx={{ mb: 2.5, display: 'flex', justifyContent: ['AI', 'MANAGER'].includes(email.sender) ? 'flex-end' : 'flex-start' }}>
                  <Box sx={{
                    maxWidth: '75%',
                    backgroundColor: (theme: any) => ['AI', 'MANAGER'].includes(email.sender)
                      ? theme.palette.background.lightGrey
                      : (theme.palette.mode === 'dark'
                        ? theme.palette.background.veryDarkGrey
                        : theme.palette.background.grey),
                    color: (theme: any) => ['AI', 'MANAGER'].includes(email.sender)
                      ? theme.palette.background.shadowBlack
                      : (theme.palette.mode === 'dark'
                        ? theme.palette.text.white
                        : theme.palette.text.primary),
                    borderRadius: '18px',
                    padding: '14px 18px',
                    position: 'relative',
                    boxShadow: ['AI', 'MANAGER'].includes(email.sender)
                      ? '0 2px 8px rgba(0, 0, 0, 0.1)'
                      : '0 2px 8px rgba(0,0,0,0.10)',
                    border: '1px solid',
                    borderColor: (theme: any) => theme.palette.mode === 'dark'
                      ? theme.palette.border.light
                      : theme.palette.border.light,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: ['AI', 'MANAGER'].includes(email.sender)
                        ? '0 4px 12px background.shadowBlack'
                        : '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }
                  }}>
                    <Typography variant="body2" sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.5,
                      fontSize: (theme: any) => theme.typography.fontSizes.body,
                      fontWeight: 400,
                      letterSpacing: '0.01em'
                    }}>
                      {email.body}
                    </Typography>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1}>
                        {email.aiJustification && (
                          <CustomTooltip title={'AI: ' + email.aiJustification}>
                            <AutoAwesome sx={{ fontSize: 24, color: ['AI', 'MANAGER'].includes(email.sender) ? 'text.secondary' : 'text.white' }} />
                          </CustomTooltip>
                        )}
                        {(email.riskLevel === 'medium' || email.riskLevel === 'high') && (
                          <CustomTooltip title={t(email.riskLevel === 'medium' ? 'Medium Risk' : 'High Risk')}>
                            <WarningAmber sx={{
                              color: 'warning.main',
                              mr: 1
                            }} />
                          </CustomTooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" sx={{
                        display: 'block',
                        textAlign: ['AI', 'MANAGER'].includes(email.sender) ? 'right' : 'left',
                        mt: 1.5,
                        opacity: ['AI', 'MANAGER'].includes(email.sender) ? 0.85 : 0.6,
                        fontSize: (theme: any) => theme.typography.fontSizes.caption,
                        fontWeight: 500,
                        letterSpacing: '0.02em'
                      }}>
                        {email.sent ? formatDateTime(email.at) : (
                          <Box component="span" sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontStyle: 'italic'
                          }}>
                            {t('Awaiting review')}
                          </Box>
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              ))}
          </Box>
        ) : (
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid border.default',
            borderRadius: 2,
            p: 6,
            backgroundColor: 'background.white',
            minHeight: 200,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.body,
              fontWeight: 400,
              opacity: 0.7
            }}>
              {t('No messages')}
            </Typography>
          </Box>
        )}
      </Box>


    </Box>
  );
};

const MessageInput = ({
  message,
  handleMessageChange,
  isTextareaScrolling,
  textareaScrollRef,
}: {
  message: string;
  handleMessageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  isTextareaScrolling: boolean;
  textareaScrollRef: React.RefObject<HTMLTextAreaElement>;
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{
      position: 'relative',
      pt: 1.5,
      '&:hover': {
        borderColor: (theme: any) => theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.12)'
          : 'rgba(0, 0, 0, 0.12)',
      },
      '&:focus-within': {
        borderColor: (theme: any) => theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(0, 0, 0, 0.16)',
      }
    }}>
      <TextField
        fullWidth
        label={t('Reply Client')}
        multiline
        rows={4}
        value={message ?? ''}
        onChange={handleMessageChange}
        placeholder={t('Enter your message to the client regarding this negotiation...')}
        inputRef={textareaScrollRef}
        variant="outlined"
        sx={{
          '& .MuiInputBase-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiInputBase-input': {
            scrollbarWidth: isTextareaScrolling ? 'thin' : 'none',
            msOverflowStyle: isTextareaScrolling ? 'scrollbar' : 'none',
            '&::-webkit-scrollbar': {
              width: isTextareaScrolling ? '8px' : '0px',
              height: isTextareaScrolling ? '8px' : '0px',
              transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
              borderRadius: '100px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme: any) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.2)',
              borderRadius: '100px',
              '&:hover': {
                backgroundColor: (theme: any) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
        }}
      />
    </Box>
  );
};
